import { useEffect, useRef, useState } from "react";
import { AmbientField, Vignette } from "@/components/landing/CinematicHeroEffects";
import { UseCasePreviewPanel } from "@/components/landing/UseCasePreviewPanel";
import { FeatureInteractionPanel, hasFeatureDetail } from "@/components/landing/FeatureInteractionPanel";
import { OrchestrationField } from "@/components/landing/OrchestrationField";
import { EnterpriseExpandablePanel } from "@/components/landing/EnterpriseExpandablePanel";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { motion, useInView } from "framer-motion";
import { getPublicPlans } from "@/billing/planRegistry";
import { NuvraLogo } from "@/components/NuvraLogo";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { usePlatformStats, isDisplayable } from "@/hooks/usePlatformStats";
import {
  Server, Database, Cloud, Cpu, Zap, Rocket, Eye, Store,
  Brain, Smartphone, BarChart3, Building2, Heart, Gamepad2, Bot,
  ArrowRight, Play, Layers, GitBranch, MonitorPlay, ShieldCheck,
  CheckCircle2, Workflow, LockKeyhole, PanelTop, Route, TestTube2,
} from "lucide-react";
import { OrchestrationPipeline } from "@/components/OrchestrationPipeline";
import { NuvraBuildShowcase } from "@/components/landing/NuvraBuildShowcase";

/* Static structure data (no text - text comes from i18n at render time) */

const USE_CASE_DATA = [
  { id: "saas",      icon: Layers,    iconColor: "text-violet-400" },
  { id: "market",    icon: Store,     iconColor: "text-cyan-400"   },
  { id: "fintech",   icon: BarChart3, iconColor: "text-emerald-400" },
  { id: "health",    icon: Heart,     iconColor: "text-rose-400"   },
  { id: "gov",       icon: Building2, iconColor: "text-blue-400"   },
  { id: "mobile",    icon: Smartphone, iconColor: "text-orange-400" },
  { id: "game",      icon: Gamepad2,  iconColor: "text-amber-400"  },
  { id: "ai",        icon: Brain,     iconColor: "text-purple-400" },
];

const FEATURE_DATA = [
  { id: "intent",   icon: Bot,       iconColor: "text-violet-400" },
  { id: "pipeline", icon: Cpu,       iconColor: "text-cyan-400"   },
  { id: "preview",  icon: Eye,       iconColor: "text-emerald-400" },
  { id: "deploy",   icon: Rocket,    iconColor: "text-orange-400" },
  { id: "healing",  icon: GitBranch, iconColor: "text-blue-400"   },
  { id: "monitor",  icon: Zap,       iconColor: "text-amber-400"  },
];

const LAYER_DATA = [
  { id: "frontend",   icon: MonitorPlay, iconColor: "text-violet-400" },
  { id: "backend",    icon: Server,      iconColor: "text-cyan-400"   },
  { id: "database",   icon: Database,    iconColor: "text-orange-400" },
  { id: "infra",      icon: Cloud,       iconColor: "text-blue-400"   },
  { id: "analytics",  icon: BarChart3,   iconColor: "text-emerald-400" },
  { id: "automation", icon: GitBranch,   iconColor: "text-amber-400"  },
];

const ARCH_DATA = [
  { id: "ai",       icon: Brain,       color: "text-violet-400",  bg: "bg-violet-500/5"  },
  { id: "deploy",   icon: Rocket,      color: "text-cyan-400",    bg: "bg-cyan-500/5"    },
  { id: "mobile",   icon: Smartphone,  color: "text-emerald-400", bg: "bg-emerald-500/5" },
  { id: "store",    icon: Store,       color: "text-orange-400",  bg: "bg-orange-500/5"  },
  { id: "exec",     icon: Cpu,         color: "text-blue-400",    bg: "bg-blue-500/5"    },
  { id: "observ",   icon: BarChart3,   color: "text-amber-400",   bg: "bg-amber-500/5"   },
  { id: "security", icon: ShieldCheck, color: "text-rose-400",    bg: "bg-rose-500/5"    },
  { id: "realtime", icon: Zap,         color: "text-yellow-400",  bg: "bg-yellow-500/5"  },
];

const STAT_DATA = [
  { id: "architecture", valueColor: "text-violet-400" },
  { id: "code",         valueColor: "text-primary"    },
  { id: "deployment",   valueColor: "text-cyan-400"   },
  { id: "delivery",     valueColor: "text-emerald-400" },
];

const PIPELINE_DATA = ["sequential", "repair", "zerotouch"];

const PRINCIPLE_DATA = [
  { id: "intent", icon: Bot, iconColor: "text-violet-400" },
  { id: "schema", icon: Workflow, iconColor: "text-cyan-400" },
  { id: "verify", icon: TestTube2, iconColor: "text-emerald-400" },
  { id: "govern", icon: LockKeyhole, iconColor: "text-orange-400" },
];

const CONTROL_SURFACES = [
  { id: "requirements", icon: Route },
  { id: "architecture", icon: Workflow },
  { id: "workOrders", icon: PanelTop },
  { id: "builds", icon: Cpu },
  { id: "environments", icon: Cloud },
  { id: "deployments", icon: Rocket },
  { id: "memory", icon: Brain },
  { id: "integrations", icon: GitBranch },
];

const CAPABILITY_DATA = [
  { id: "mobile", icon: Smartphone, iconColor: "text-emerald-400" },
  { id: "publishing", icon: Rocket, iconColor: "text-violet-400" },
  { id: "externalDeploy", icon: Cloud, iconColor: "text-cyan-400" },
  { id: "workspace", icon: PanelTop, iconColor: "text-blue-400" },
  { id: "memory", icon: Brain, iconColor: "text-amber-400" },
  { id: "integrations", icon: GitBranch, iconColor: "text-rose-400" },
];

const FAQ_DATA = ["what", "real", "control", "deploy", "ownership"];

const CHIP_DATA = [
  { id: "saas",     matchId: "saas"    },
  { id: "fintech",  matchId: "fintech" },
  { id: "market",   matchId: "market"  },
  { id: "mobileApp", matchId: "mobile" },
  { id: "enterprise", matchId: null   },
  { id: "aiTool",   matchId: "ai"      },
  { id: "gamePlatform", matchId: "game" },
  { id: "dashboard", matchId: "ai"    },
];

/* ─── Fade-in wrapper ─── */
function Reveal({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Navbar ───
   Exported so the responsive suite can render the real header and MEASURE it, rather than
   reading intent off its Tailwind classes. The export changes nothing about what renders. */
export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { session } = useAuth();
  const { t } = useI18n();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm" : "bg-transparent"}`}>
      {/* 56px on a phone, 64px from `sm` up. The two standard app-bar heights, not a compressed
          desktop bar: the desktop proportion is preserved byte for byte above 640px. */}
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 sm:px-6 h-14 sm:h-16">

        {/* Brand */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <NuvraLogo size={28} className="shrink-0" decorative />
          <span className="text-[17px] font-bold tracking-tight gradient-text">Nuvra</span>
        </Link>

        {/* Nav links - hidden on mobile */}
        <div className="hidden md:flex items-center gap-7 text-sm text-muted-foreground/80">
          <button onClick={() => scrollTo("features")} className="hover:text-foreground transition-colors">{t("landing.nav.features")}</button>
          <button onClick={() => scrollTo("how-it-works")} className="hover:text-foreground transition-colors">{t("landing.nav.howItWorks")}</button>
          <button onClick={() => scrollTo("use-cases")} className="hover:text-foreground transition-colors">{t("landing.nav.useCases")}</button>
          <button onClick={() => scrollTo("system-architecture")} className="hover:text-foreground transition-colors">{t("landing.nav.architecture")}</button>
          <Link to="/pricing" className="hover:text-foreground transition-colors">{t("landing.nav.pricing")}</Link>
        </div>

        {/* CTA + mobile navigation */}
        <div className="flex items-center gap-2">
          {session ? (
            /* A signed-in visitor lands on the dashboard composer, so the header says what they
               go there to do rather than naming the screen. Same destination, honest label. */
            /* Sized deliberately under the hero CTA it shares a label with: the loudest button on
               the page has to be the one in the hero, not the one in the chrome above it. The glow
               is restrained at rest and only opens up on hover, for the same reason.

               ON A PHONE IT IS TALLER AND NARROWER, which is the opposite of shrinking it. Measured
               at 360px, this pill was 140x32 while the brand lockup beside it was 93x28 and the
               "Sign in" button it stands in for was 74x36: half again as wide as the brand it is
               meant to sit under, and the shortest thing in the header to actually tap. Below `sm`
               it takes the header's own action height (36px, the same `Button size="sm"` box as
               "Sign in"), tighter padding, and no trailing arrow, so it reads as a control rather
               than a banner. The arrow is decoration on a surface with no room for it; the hero CTA
               keeps its own. Everything from `sm` up is unchanged. */
            <Link
              to="/dashboard"
              className="group inline-flex h-9 sm:h-8 items-center gap-1.5 rounded-full gradient-primary px-3 sm:px-3.5 text-xs font-semibold text-white shadow-[0_1px_10px_-3px_hsl(var(--primary)/0.45)] transition-all hover:shadow-[0_4px_18px_-3px_hsl(var(--primary)/0.7)] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {t("landing.hero.ctaPrimary")}
              <ArrowRight className="hidden sm:block h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </Link>
          ) : (
            <Button size="sm" variant="outline" className="h-9" asChild>
              <Link to="/login">{t("landing.nav.signIn")}</Link>
            </Button>
          )}
        </div>

      </div>


    </nav>
  );
}

/* ─── Real platform statistics ───
   Renders measured counts, and renders NOTHING at all when they are unavailable or still too
   small to be informative. There is no loading skeleton and no placeholder number by design: a
   marketing page is precisely where an invented figure does the most damage, so the only two
   states here are "a real number" and "nothing". */
function PlatformStatsRow({ compact = false }: { compact?: boolean }) {
  const { stats } = usePlatformStats();
  const { t } = useI18n();
  if (!stats) return null;

  const entries = [
    { id: "builders",   value: stats.builders,        display: stats.display.builders,        color: "text-violet-400"  },
    { id: "projects",   value: stats.projectsCreated, display: stats.display.projectsCreated, color: "text-primary"     },
    { id: "builds",     value: stats.buildsSucceeded, display: stats.display.buildsSucceeded, color: "text-cyan-400"    },
    { id: "deployments",value: stats.deployments,     display: stats.display.deployments,     color: "text-emerald-400" },
  ].filter(e => isDisplayable(e.value));

  // Every count is still below the threshold. Show nothing rather than a thin, volatile row.
  if (entries.length === 0) return null;

  const row = (
    <div className={compact ? "flex flex-wrap justify-center gap-5 sm:gap-8 mt-0" : "flex flex-wrap justify-center gap-6 sm:gap-10 mt-6 sm:mt-8"}>
      {entries.map(e => (
        <div key={e.id} className="text-center">
          <p className={`text-2xl font-bold ${e.color}`}>{e.display}</p>
          <p className="text-xs text-muted-foreground">{t(`landing.live.${e.id}`)}</p>
        </div>
      ))}
    </div>
  );

  if (!compact) return row;
  return (
    <section aria-label={t("landing.proof.label")} className="relative border-y border-border/30 bg-card/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-7">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-primary/80">{t("landing.proof.label")}</p>
            <p className="text-sm text-muted-foreground mt-1">{t("landing.proof.desc")}</p>
          </div>
          {row}
        </div>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export default function LandingPage() {
  const { t } = useI18n();

  const useCases = USE_CASE_DATA.map(uc => ({
    ...uc,
    title: t(`landing.useCase.${uc.id}.title`),
    desc:  t(`landing.useCase.${uc.id}.desc`),
  }));

  const [selectedUseCase, setSelectedUseCase] = useState<typeof useCases[0] | null>(null);
  const [useCasePanelOpen, setUseCasePanelOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
  const [featurePanelOpen, setFeaturePanelOpen] = useState(false);

  return (
    <main className="min-h-screen bg-background text-foreground">
      <UseCasePreviewPanel useCase={selectedUseCase} open={useCasePanelOpen} onOpenChange={setUseCasePanelOpen} />
      <FeatureInteractionPanel featureTitle={selectedFeature} open={featurePanelOpen} onOpenChange={setFeaturePanelOpen} />
      <Navbar />

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, hsl(228 25% 6%) 0%, hsl(250 30% 8%) 30%, hsl(240 25% 5%) 60%, hsl(228 14% 5%) 100%)" }}
      >
        <AmbientField />
        <Vignette />

        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] rounded-full blur-[220px] pointer-events-none"
          style={{ background: "radial-gradient(ellipse, hsl(255 50% 25% / 0.22) 0%, transparent 70%)" }}
        />

        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 relative z-10 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12 xl:gap-20 pt-24 sm:pt-32 pb-8 sm:pb-12 lg:pb-0">
          <div className="w-full lg:max-w-[470px] flex-shrink-0 text-center lg:text-left">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-primary mb-4">{t("landing.hero.badge")}</span>
              <h1 className="text-4xl sm:text-5xl xl:text-6xl font-extrabold leading-[1.02] tracking-tight mb-5">
                {t("landing.hero.h1a")}{" "}
                <span className="bg-gradient-to-r from-primary via-violet-400 to-nuvra-accent bg-clip-text text-transparent">{t("landing.hero.h1b")}</span>
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-5 leading-relaxed">
                {t("landing.hero.desc")}
              </p>
              <div className="mt-5 flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-2 text-[11px] text-muted-foreground">
                {["architecture", "code", "validation", "control"].map((id) => (
                  <span key={id} className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400/80" />
                    {t(`landing.hero.proof.${id}`)}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>

          <div className="w-full lg:flex-1 min-w-0 lg:pt-2">
            <div className="relative">
              <div className="absolute -inset-8 rounded-[2rem] bg-primary/10 blur-3xl pointer-events-none" />
              <div className="relative">
                <OrchestrationField />
              </div>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, transparent, hsl(228 14% 5%))" }}
        />
      </section>

      {/* PROOF STRIP: only measured platform numbers render. */}
      <PlatformStatsRow compact />

      {/* PRODUCT SHOWCASE */}
      <NuvraBuildShowcase />

      {/* PRODUCT EXPLANATION */}
      <section className="py-12 sm:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/25 to-transparent" />
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] rounded-full blur-[120px] pointer-events-none opacity-40"
          style={{ background: "radial-gradient(ellipse, hsl(255 50% 30% / 0.3) 0%, transparent 70%)" }}
        />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <Reveal>
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">The Nuvra difference</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">{t("landing.explain.h2")}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-lg">
              {t("landing.explain.desc")}
            </p>
          </Reveal>

          {/* Real measured counts, shown only once they are large enough to mean something.
              Below that the capability row below stands on its own. Nothing here is ever a
              placeholder or a rounded-up figure: see usePlatformStats for why. */}
          <Reveal delay={0.15}>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mt-6 sm:mt-10">
              {STAT_DATA.map(stat => (
                <div key={stat.id} className="text-center">
                  <p className={`text-2xl font-bold ${stat.valueColor}`}>{t(`landing.stat.${stat.id}.value`)}</p>
                  <p className="text-xs text-muted-foreground">{t(`landing.stat.${stat.id}.label`)}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <div className="mt-8 sm:mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {CHIP_DATA.map((chip, i) => (
              <Reveal key={chip.id} delay={i * 0.05}>
                <div
                  className="rounded-xl border border-border/50 bg-card/60 backdrop-blur p-4 text-sm font-medium text-foreground hover:border-primary/50 hover:bg-card/80 transition-all cursor-pointer hover-lift"
                  onClick={() => {
                    const match = useCases.find(u => u.id === chip.matchId);
                    if (match) { setSelectedUseCase(match); setUseCasePanelOpen(true); }
                  }}
                >
                  {t(`landing.chip.${chip.id}`)}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENGINEERING PRINCIPLES */}
      <section id="principles" className="py-12 sm:py-24 relative overflow-hidden border-t border-border/20">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_0%,hsl(255_55%_45%/0.08),transparent_55%)]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <Reveal className="text-center mb-10 sm:mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.principles.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">{t("landing.principles.h2")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t("landing.principles.desc")}</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRINCIPLE_DATA.map((item, i) => (
              <Reveal key={item.id} delay={i * 0.06}>
                <div className="group h-full rounded-2xl border border-border/40 bg-card/40 backdrop-blur p-5 transition-all hover:-translate-y-1 hover:border-primary/40 hover:bg-card/70 hover:shadow-[0_18px_50px_-30px_hsl(var(--primary)/0.7)]">
                  <div className="flex items-center justify-between mb-5">
                    <item.icon className={`h-6 w-6 ${item.iconColor}`} />
                    <CheckCircle2 className="h-4 w-4 text-emerald-400/70" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t(`landing.principle.${item.id}.title`)}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{t(`landing.principle.${item.id}.desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-12 sm:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/25 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.howItWorks.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-3">{t("landing.howItWorks.liveH2")}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t("landing.howItWorks.desc")}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <OrchestrationPipeline variant="landing" />
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-8 grid sm:grid-cols-3 gap-4 text-center">
              {PIPELINE_DATA.map(id => (
                <div key={id} className="p-4 rounded-xl border border-border/20 bg-card/30">
                  <p className="text-sm font-semibold text-foreground mb-1">{t(`landing.pipeline.${id}.label`)}</p>
                  <p className="text-xs text-muted-foreground">{t(`landing.pipeline.${id}.desc`)}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2} className="mt-10">
            <div className="rounded-2xl border border-border/50 bg-card/30 overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-border/40 bg-muted/20">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_hsl(160_70%_50%/0.7)]" />
                  <span className="text-xs font-semibold text-foreground">{t("landing.lifecycle.label")}</span>
                </div>
                <span className="text-[10px] text-muted-foreground">{t("landing.lifecycle.summary")}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-px bg-border/30">
                {[
                  "landing.lifecycle.intent",
                  "landing.lifecycle.architecture",
                  "landing.lifecycle.database",
                  "landing.lifecycle.backend",
                  "landing.lifecycle.frontend",
                  "landing.lifecycle.validation",
                  "landing.lifecycle.deployment",
                  "landing.lifecycle.production",
                ].map((stage, i) => (
                  <motion.div
                    key={stage}
                    initial={{ opacity: 0.35 }}
                    animate={{ opacity: [0.35, 1, 0.55] }}
                    transition={{ duration: 2.8, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
                    className="bg-background/80 px-3 py-4 text-center"
                  >
                    <div className="mx-auto mb-2 h-6 w-6 rounded-full border border-primary/30 bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary">{i + 1}</div>
                    <p className="text-[11px] font-medium text-foreground">{t(stage)}</p>
                  </motion.div>
                ))}
              </div>
              <p className="px-4 sm:px-5 py-2.5 text-[10px] text-muted-foreground/70">
                {t("landing.lifecycle.note")}
              </p>
            </div>
          </Reveal>

        </div>
      </section>


      {/* SYSTEM SCALE */}
      <section className="py-12 sm:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/25 to-transparent" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <Reveal>
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.scale.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">{t("landing.scale.h2")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8 sm:mb-12">
              {t("landing.scale.desc")}
            </p>
          </Reveal>
          <div
            className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4"
            data-testid="landing-system-scale-grid"
          >
            {LAYER_DATA.map((item) => (
              <div
                key={item.id}
                className="min-w-0 flex flex-col items-center justify-center gap-3 p-4 sm:p-6 rounded-2xl border border-border/40 bg-card/40 transition-colors hover:border-border/70 hover:bg-card/60"
              >
                <item.icon className={`h-7 w-7 sm:h-8 sm:w-8 ${item.iconColor}`} />
                <span className="text-xs sm:text-sm font-medium text-foreground text-center leading-tight">
                  {t(`landing.layer.${item.id}`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section id="use-cases" className="py-12 sm:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/25 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-10 sm:mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.useCases.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3">{t("landing.useCases.h2")}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {useCases.map((uc, i) => (
              <Reveal key={uc.id} delay={i * 0.05}>
                <Card
                  className="border-border/40 bg-card/50 backdrop-blur hover:border-primary/50 hover:bg-card/80 hover:shadow-[0_0_24px_hsl(var(--primary)/0.08)] transition-all h-full cursor-pointer hover-lift"
                  onClick={() => { setSelectedUseCase(uc); setUseCasePanelOpen(true); }}
                >
                  <CardContent className="p-5">
                    <uc.icon className={`h-8 w-8 ${uc.iconColor} mb-3`} />
                    <h3 className="font-semibold text-foreground mb-1">{uc.title}</h3>
                    <p className="text-sm text-muted-foreground">{uc.desc}</p>
                    <span className="inline-flex items-center gap-1 mt-3 text-xs text-primary font-medium">
                      {t("landing.useCases.exploreTemplate")} <ArrowRight className="h-3 w-3" />
                    </span>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-12 sm:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/25 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-10 sm:mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.features.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3">{t("landing.features.h2")}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURE_DATA.map((f, i) => (
              <Reveal key={f.id} delay={i * 0.06}>
                {/* Interactive only where a panel genuinely opens. Four of these six titles
                    have no entry in FeatureInteractionPanel, so the card used to advertise
                    "Learn more", take a click, and show nothing. The card still says what the
                    capability is; it just stops promising a dialog it cannot produce. */}
                <div
                  className={`p-6 rounded-2xl border border-border/40 bg-card/40 backdrop-blur transition-all ${
                    hasFeatureDetail(t(`landing.feature.${f.id}.title`))
                      ? "cursor-pointer hover-lift hover:border-primary/50 hover:bg-card/70 hover:shadow-[0_0_24px_hsl(var(--primary)/0.08)]"
                      : "hover:border-border/70 hover:bg-card/60"
                  }`}
                  onClick={() => {
                    const title = t(`landing.feature.${f.id}.title`);
                    if (!hasFeatureDetail(title)) return;
                    setSelectedFeature(title); setFeaturePanelOpen(true);
                  }}
                >
                  <f.icon className={`h-8 w-8 ${f.iconColor} mb-4`} />
                  <h3 className="font-semibold text-foreground mb-2">{t(`landing.feature.${f.id}.title`)}</h3>
                  <p className="text-sm text-muted-foreground">{t(`landing.feature.${f.id}.desc`)}</p>
                  {hasFeatureDetail(t(`landing.feature.${f.id}.title`)) && (
                    <span className="inline-flex items-center gap-1 mt-3 text-xs text-primary font-medium">
                      {t("landing.features.learnMore")} <ArrowRight className="h-3 w-3" />
                    </span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BEYOND THE FIRST BUILD */}
      <section className="py-12 sm:py-24 relative overflow-hidden border-t border-border/20">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_50%_0%,hsl(255_55%_45%/0.07),transparent_55%)]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <Reveal className="text-center mb-10 sm:mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.capabilities.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">{t("landing.capabilities.h2")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t("landing.capabilities.desc")}</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CAPABILITY_DATA.map((item, i) => (
              <Reveal key={item.id} delay={i * 0.05}>
                <div className="group h-full rounded-2xl border border-border/40 bg-card/40 backdrop-blur p-5 transition-all hover:-translate-y-1 hover:border-primary/40 hover:bg-card/70">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                    <item.icon className={`h-5 w-5 ${item.iconColor}`} />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{t(`landing.capability.${item.id}.title`)}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{t(`landing.capability.${item.id}.desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT CONTROL PLANE */}
      <section className="py-12 sm:py-24 relative overflow-hidden border-t border-border/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-14 items-center">
            <Reveal>
              <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.control.label")}</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">{t("landing.control.h2")}</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">{t("landing.control.desc")}</p>
              <Button asChild variant="outline">
                <Link to="/signup">{t("landing.control.cta")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="rounded-2xl border border-border/50 bg-card/50 backdrop-blur overflow-hidden shadow-[0_24px_90px_-50px_hsl(var(--primary)/0.7)]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border/40 bg-muted/20">
                  <div className="flex items-center gap-2">
                    <NuvraLogo size={17} decorative />
                    <span className="text-xs font-semibold">{t("landing.control.window")}</span>
                  </div>
                  <span className="text-[10px] text-emerald-400">{t("landing.control.status")}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4">
                  {CONTROL_SURFACES.map((item, i) => (
                    <div key={item.id} className="min-h-[88px] border-b border-r border-border/30 p-3.5 flex flex-col justify-between hover:bg-primary/5 transition-colors">
                      <item.icon className="h-4 w-4 text-primary" />
                      <div>
                        <p className="text-xs font-semibold text-foreground">{t(`landing.control.surface.${item.id}.title`)}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{t(`landing.control.surface.${item.id}.desc`)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-3 bg-background/60 text-[10px] text-muted-foreground flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  {t("landing.control.note")}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST / ENTERPRISE */}
      <section className="py-12 sm:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/25 to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.enterprise.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-2">{t("landing.enterprise.h2")}</h2>
            <p className="text-muted-foreground text-sm">{t("landing.enterprise.hint")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <EnterpriseExpandablePanel />
          </Reveal>
        </div>
      </section>

      {/* SYSTEM ARCHITECTURE */}
      <section id="system-architecture" className="py-12 sm:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/25 to-transparent" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-10 sm:mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.arch.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">{t("landing.arch.h2")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t("landing.arch.desc")}
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ARCH_DATA.map((tier, i) => (
              <Reveal key={tier.id} delay={i * 0.05}>
                <div className={`p-5 rounded-2xl border border-border/40 ${tier.bg} backdrop-blur hover:border-primary/40 transition-all`}>
                  <div className="flex items-center gap-2.5 mb-3">
                    <tier.icon className={`h-5 w-5 ${tier.color} shrink-0`} />
                    <span className="text-sm font-semibold text-foreground">{t(`landing.arch.${tier.id}.label`)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t(`landing.arch.${tier.id}.desc`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section id="pricing" className="py-12 sm:py-24 relative overflow-hidden border-t border-border/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-10 sm:mb-12">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.pricing.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">{t("landing.pricing.h2")}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{t("landing.pricing.desc")}</p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4">
            {getPublicPlans().map((plan, i) => (
              <Reveal key={plan.tier} delay={i * 0.05}>
                <div className={`relative h-full rounded-xl sm:rounded-2xl border p-3 sm:p-5 bg-card/50 backdrop-blur transition-all hover:-translate-y-1 hover:border-primary/40 hover:bg-card/80 ${plan.mostPopular ? "border-primary/50 shadow-[0_16px_50px_-35px_hsl(var(--primary)/0.9)]" : "border-border/40"}`}>
                  {plan.mostPopular && <span className="absolute -top-2 left-2.5 sm:-top-2.5 sm:left-3 rounded-full bg-primary px-2 sm:px-2.5 py-0.5 sm:py-1 text-[8px] sm:text-[9px] font-bold text-primary-foreground">{t("landing.pricing.popular")}</span>}
                  <p className="text-[9px] sm:text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{plan.label}</p>
                  <p className="text-lg sm:text-2xl font-bold mt-1.5 sm:mt-2">{plan.monthlyPriceCents === -1 ? t("landing.pricing.custom") : plan.monthlyPriceDisplay}</p>
                  <p className="text-[9px] sm:text-[10px] text-muted-foreground mt-0.5 sm:mt-1">{plan.monthlyPriceCents > 0 ? t("landing.pricing.month") : t("landing.pricing.noMonthly")}</p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground mt-2 sm:mt-3 leading-snug sm:leading-relaxed">{plan.tagline}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="text-center mt-7">
            <Button asChild variant="outline">
              <Link to="/pricing">{t("landing.pricing.cta")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-24 relative overflow-hidden border-t border-border/20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest uppercase text-primary">{t("landing.faq.label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-3">{t("landing.faq.h2")}</h2>
            <p className="text-muted-foreground">{t("landing.faq.desc")}</p>
          </Reveal>
          <div className="space-y-3">
            {FAQ_DATA.map((id, i) => (
              <Reveal key={id} delay={i * 0.04}>
                <details className="group rounded-xl border border-border/40 bg-card/40 backdrop-blur px-4 sm:px-5 open:border-primary/30 open:bg-card/60">
                  <summary className="cursor-pointer list-none py-4 flex items-center justify-between gap-4 text-sm sm:text-base font-semibold text-foreground">
                    {t(`landing.faq.${id}.q`)}
                    <ArrowRight className="h-4 w-4 shrink-0 text-primary transition-transform duration-200 group-open:rotate-90" />
                  </summary>
                  <p className="pb-4 pr-8 text-sm leading-relaxed text-muted-foreground">{t(`landing.faq.${id}.a`)}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-12 sm:py-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/25 to-transparent" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-primary/8 blur-[140px]" />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <Reveal>
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-6">
              {t("landing.cta.h2a")}{" "}
              <span className="bg-gradient-to-r from-primary to-nuvra-accent bg-clip-text text-transparent">
                {t("landing.cta.h2b")}
              </span>
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              {t("landing.cta.desc")}
            </p>
            {/* One action. The page has already shown the visitor the architecture field, the
                pipeline and the planner; a second button here competes with the decision rather
                than supporting it, and "See a live generation" only sent them back up the page
                they just read. */}
            <div className="flex justify-center">
              <Button size="lg" asChild>
                <Link to="/signup">{t("landing.cta.buildWithNuvra")} <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
